import 'es6-promise/auto';
import Vue from 'vue';
import router, { handleRoutes } from './front/routes';
import App from './front/App.vue';
import store from './stores/front';
import { getModule } from 'vuex-module-decorators';
import UserModule from './stores/modules/user';
import FoodModule from './stores/modules/food';
import TimeSchedulerModule from './stores/modules/time-scheduler';
import CartModule from './stores/modules/cart';
import { polyfill as smoothScrollPolyfill } from 'smoothscroll-polyfill';
import { setupAccessibleOutline } from '@/utils/utilities';
import setupCookieBanner from '@/utils/cookie-banner';
import { setupOnlineStateManagement } from '@/utils/online-state-manager';
import { isStoreOpen } from '@/utils/store-schedule';
import messaging from '@/utils/firebase-config';
import { getToken } from 'firebase/messaging';
import * as Sentry from '@sentry/vue';
import registerPWAEvents from '@/utils/pwa-events';
import { initServiceWorkers } from '@/utils/service-workers';
import { resetCacheForUpdate } from '@/utils/service-workers';
import axios from 'axios';
require('jspolyfill-array.prototype.findIndex');
require('jspolyfill-array.prototype.find');

// utils
resetCacheForUpdate();
setupCookieBanner();
smoothScrollPolyfill();
setupAccessibleOutline();
setupOnlineStateManagement(store);
registerPWAEvents();
initServiceWorkers();
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const token = document.head.querySelector('meta[name="csrf-token"]');

if (token) {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = (token as HTMLMetaElement).content;
} else {
    console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token');
}

axios.interceptors.response.use((response) => {
    if (response.headers['x-csrf-token']) {
        const newCsrfToken = response.headers['x-csrf-token'];
        axios.defaults.headers.common['X-CSRF-TOKEN'] = newCsrfToken;
        const tokenMeta = document.head.querySelector('meta[name="csrf-token"]');
        if (tokenMeta) {
            (tokenMeta as HTMLMetaElement).content = newCsrfToken;
        }
    }
    return response;
});

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response.status === 419 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const response = await axios.get('/api/csrf-token');
                const newCsrfToken = response.data.token;
                axios.defaults.headers.common['X-CSRF-TOKEN'] = newCsrfToken;
                originalRequest.headers['X-CSRF-TOKEN'] = newCsrfToken;
                const tokenMeta = document.head.querySelector('meta[name="csrf-token"]');
                if (tokenMeta) {
                    (tokenMeta as HTMLMetaElement).content = newCsrfToken;
                }
                return axios(originalRequest);
            } catch (e) {
                window.location.reload();
            }
        }
        if (error.response.status === 401) {
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

Sentry.init({
    Vue,
    dsn: 'https://a1630dc807fd2faf09d262f537b60e82@o563061.ingest.us.sentry.io/4507732783071232',
    integrations: [Sentry.browserTracingIntegration()],
    environment: process.env.APP_ENV,
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
});

const app = new Vue({
    el: '#v-app',
    store,
    router,
    components: { App },

    async mounted() {
        const isOpen = await isStoreOpen();
        const foodModule: FoodModule = getModule(FoodModule, this.$store);
        const userModule: UserModule = getModule(UserModule, this.$store);
        await userModule.fetchUser();

        handleRoutes();

        if (this.$route.query.reset_token !== undefined) {
            userModule.setupResetPassword(<string>this.$route.query.reset_token);
        }

        if (!isOpen) {
            foodModule.setIsReady();
            userModule.setIsReady();
            return;
        }

        const timeSchedulerModule: TimeSchedulerModule = getModule(TimeSchedulerModule, this.$store);
        const cartModule: CartModule = getModule(CartModule, this.$store);
        timeSchedulerModule.fetchTimeSlots();
        await timeSchedulerModule.fetchRetrievalMethods();
        await timeSchedulerModule.fetchSchedulingOptions();
        await timeSchedulerModule.fetchDeliveryTime();
        await foodModule.fetchProductPages();
        await foodModule.fetchOrderFlows();
        await foodModule.setProductsFromProductPages();
        await timeSchedulerModule.fetchSchedules();
        cartModule.fetchMaxOrderProducts();
        foodModule.setIsReady();
        userModule.setIsReady();
        this.$watch(
            () => this.$store.state.user.currentUser,
            async (user) => {
                if (user) {
                    try {
                        const token = await getToken(messaging, {
                            vapidKey: 'BOS2Ru2eg95aR9nvqDckFJTRbCwlCfWkSiJHQEqg_wrQSfz5mwOdb-5TpclKI3rBCM3TNrOA43QS8_RptxGkKas',
                        });
                        if (user.fcm_token !== token) {
                            user.fcm_token = token;
                            user.update();
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            },
            { deep: true, immediate: true }
        );
    },
});
